/**
 * i18n：zh-CN / en 双语。UI 字符串走 t(key)，
 * 数据表内联双语走 tl(l10n)。
 */
import type { L10n, Lang } from '../core/types';
import { ZH } from './zh';
import { EN } from './en';
import { RU } from './ru';
import { RU_DATA } from './ru.data';

export const LANGS: { id: Lang; label: string }[] = [
  { id: 'ru', label: 'Русский' },
  { id: 'en', label: 'English' },
  { id: 'zh', label: '中文' },
];
const DICTS: Record<Lang, Record<string, string>> = { zh: ZH, en: EN, ru: RU };
const LANG_KEY = 'paradise-isle:lang';

/** Язык по умолчанию: сохранённый выбор, иначе язык браузера (ru → русский, zh → китайский, иначе английский). */
export function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved === 'ru' || saved === 'en' || saved === 'zh') return saved;
  } catch { /* localStorage недоступен */ }
  const nav = (typeof navigator !== 'undefined' ? navigator.language : '') || '';
  const l = nav.toLowerCase();
  if (l.startsWith('ru') || l.startsWith('uk') || l.startsWith('be') || l.startsWith('kk') || l.startsWith('ky')) return 'ru';
  if (l.startsWith('zh')) return 'zh';
  return 'en';
}

let currentLang: Lang = detectLang();

export function setLang(lang: Lang): void {
  currentLang = lang;
  try { localStorage.setItem(LANG_KEY, lang); } catch { /* ignore */ }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang === 'zh' ? 'zh-CN' : lang;
    document.title = DICTS[lang]['start.title'] ?? 'Paradise Isle';
  }
}

export function getLang(): Lang {
  return currentLang;
}

export function t(key: string, vars?: Record<string, string | number>): string {
  let s = DICTS[currentLang][key] ?? EN[key] ?? ZH[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      s = s.replaceAll(`{${k}}`, String(v));
    }
  }
  return s;
}

export function tl(l10n: L10n | undefined): string {
  if (!l10n) return '';
  if (currentLang === 'zh') return l10n.zh;
  if (currentLang === 'ru') return l10n.ru ?? RU_DATA[l10n.en] ?? l10n.en ?? l10n.zh;
  return l10n.en || l10n.zh;
}

/** 毫秒 → "2小时3分" / "45秒" */
export function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (currentLang === 'zh') {
    if (h > 0) return m > 0 ? `${h}小时${m}分` : `${h}小时`;
    if (m > 0) return sec > 0 && m < 5 ? `${m}分${sec}秒` : `${m}分钟`;
    return `${sec}秒`;
  }
  if (currentLang === 'ru') {
    if (h > 0) return m > 0 ? `${h} ч ${m} мин` : `${h} ч`;
    if (m > 0) return sec > 0 && m < 5 ? `${m} мин ${sec} с` : `${m} мин`;
    return `${sec} с`;
  }
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return sec > 0 && m < 5 ? `${m}m ${sec}s` : `${m}m`;
  return `${sec}s`;
}
